import React from 'react'
import { motion } from 'framer-motion'

const companyValues = [
  {
    title: "Experience",
    description: "With our experience in the field, you can rely on us. We understand that every project is unique. We listen and adapt to suit your specific needs.",
    bar: "bg-orange-500",
    hover: "hover:border-orange-300 hover:shadow-orange-100",
  },
  {
    title: "Quality Work",
    description: "We build strong working relationships through our quality of work and client satisfactory results.",
    bar: "bg-red-500",
    hover: "hover:border-red-300 hover:shadow-red-100",
  },
  {
    title: "Fast Turnaround",
    description: "Pushing boundaries to elevate your business and deliver you the solution with quick turnaround time.",
    bar: "bg-amber-500",
    hover: "hover:border-amber-300 hover:shadow-amber-100",
  },
  {
    title: "Ideas",
    description: "Ideas are what inspire, generate momentum and create curiosity. We just happen to be in the business of making them happen for you.",
    bar: "bg-rose-500",
    hover: "hover:border-rose-300 hover:shadow-rose-100",
  },
]

const uniquePoints = [
  "Surpassing Quality of the services we provide, makes us different from others. We have highly skilled personnel in a special team of Quality Control.",
  "We forward our work through a strict quality check under controlled environments before forwarding it to our clients, so that they avail only the best Work within short time frame.",
  "We are fully equipped with ultra modern technologies, so we ensure the quality by passing it through multilayer procedure whether it is running the prepared files in different machines or stitching the prepared design on relevant fabric by our machine.",
]

const reveal = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, delay },
})

const Accent = ({ children }) => (
  <span className="bg-gradient-to-r from-orange-500 via-red-500 to-amber-500 bg-clip-text text-transparent">
    {children}
  </span>
)

/* Heading on the left, content on the right */
const Split = ({ title, children, tint = false }) => (
  <section className={`relative overflow-hidden py-20 md:py-28 ${tint ? 'bg-stone-50' : 'bg-white'}`}>
    <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-16">
      <motion.h2
        {...reveal()}
        className="lg:col-span-2 text-4xl md:text-5xl font-extrabold tracking-tight leading-[1.05] text-slate-900 lg:sticky lg:top-28 self-start"
      >
        {title}
      </motion.h2>
      <motion.div {...reveal(0.1)} className="lg:col-span-3 space-y-6 text-lg leading-relaxed text-slate-600">
        {children}
      </motion.div>
    </div>
  </section>
)

const CompanyPage = () => {
  return (
    <div className="pt-20 min-h-screen bg-white text-slate-900 antialiased">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-slate-900 text-white">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-30"
          style={{ backgroundImage: 'radial-gradient(60rem 28rem at 85% -10%, rgba(249,115,22,.35), transparent 60%)' }}
        />
        <svg
          aria-hidden="true"
          className="absolute bottom-10 left-0 w-full h-24 text-[#F97316]/40"
          viewBox="0 0 1200 96"
          preserveAspectRatio="none"
          fill="none"
        >
          <path
            d="M0 60 C 200 10, 400 110, 600 55 S 1000 10, 1200 60"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeDasharray="10 9"
            strokeLinecap="round"
          />
        </svg>
        <div className="relative max-w-6xl mx-auto px-6 py-28 md:py-36">
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-5xl md:text-7xl font-extrabold tracking-tight leading-[1.05] max-w-4xl"
          >
            About <span className="text-[#F97316]">Vector Visionary</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-6 text-lg md:text-xl text-slate-300 max-w-xl leading-relaxed"
          >
            Professional vector art services for logos, embroidery, and custom patches
          </motion.p>
        </div>
      </section>

      {/* About Us Section */}
      <Split title={<>About <Accent>Us</Accent></>}>
        <p>
          Vector Visionary is a provider of custom embroidery digitizing services for badges, uniforms, and apparel. Our business is custom embroidery digitizing, logo designing, graphics & vectorization. We recognize the unique aspects of every design.
        </p>
        <p>
          Our experienced digitizers manually convert your design into the digitized embroidery file format of your choice. Because we do not scan or "auto-digitize," we are able to adhere to the highest level of quality and capture the unique features of your design.
        </p>
        <p>
          We welcome small, one time orders but have the capability to handle large quantities. We provide digitizing to individuals doing single run embroidery projects and small monogram and screen printing shops to large national uniform service companies.
        </p>
        <blockquote className="rounded-2xl border-l-4 border-[#F97316] bg-orange-50 px-6 py-5 text-xl font-semibold leading-snug text-[#EA580C]">
          "All of us at Vector Visionary would like to thank our customers - for your trust and support."
        </blockquote>
      </Split>

      {/* Mission Section */}
      <section className="bg-white px-6 pb-20 md:pb-28">
        <motion.div
          {...reveal()}
          className="relative max-w-6xl mx-auto overflow-hidden rounded-[2rem] bg-slate-900 px-8 py-16 md:px-16 md:py-24 text-white"
        >
          <div aria-hidden="true" className="pointer-events-none absolute -top-16 -right-16 h-72 w-72 rounded-full bg-orange-500/30 blur-3xl" />
          <div aria-hidden="true" className="pointer-events-none absolute inset-3 rounded-[1.5rem] border-2 border-dashed border-orange-400/30" />
          <div className="relative max-w-3xl">
            <h2 className="text-4xl md:text-6xl font-extrabold tracking-tight leading-[1.05] mb-8">
              Our <span className="text-[#F97316]">Mission</span>
            </h2>
            <p className="text-xl md:text-3xl font-medium leading-snug text-slate-200">
              The mission at Vector Visionary is to provide premium quality services at the most affordable pricing for clients from across the world.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Vision Section */}
      <Split title={<>Our <Accent>Vision</Accent></>} tint>
        <p>
          As specialised designers, every new project drives us to challenge the boundaries of designing. Innovation and creative thinking lies at the heart of what we do: taking a new concept right from the initial stages of thinking, through to scratching, design implementation, quality inspection, support.
        </p>
        <p>
          By continually investing in our people, processes and clients we provide high-quality, superior solutions to our customers, allowing them to shape the future of their industries.
        </p>
      </Split>

      {/* What We Do Section */}
      <Split title={<>What <Accent>We Do</Accent>?</>}>
        <p>
          If you are new to Embroidery digitizing, let us describe you about what we do. We will collect logo, pattern, photo or any other creative artwork from you and will convert it into a digital file in certain format/language that the embroidery machine can execute and stitch it out.
        </p>
        <p>
          This process is carried out by our thoroughly knowledgeable professionals with the help of certain embroidery digitizing software. It requires skilled digitizer's manual input and creative treatments as per the clients requirement which can not be achieved by any auto-digitizing software. In fact, many of our clients sent us auto-digitized file and asked us to re-digitize it.
        </p>
      </Split>

      {/* Company Values */}
      <section className="bg-stone-50 py-20 md:py-28 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {companyValues.map((value, index) => (
            <motion.div
              key={value.title}
              {...reveal(index * 0.1)}
              className={`group relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-8 pt-10 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl ${value.hover}`}
            >
              <div className={`absolute left-0 top-0 h-1.5 w-full ${value.bar}`} />
              <h3 className="text-2xl font-bold tracking-tight mb-3">{value.title}</h3>
              <div className="mb-4 border-t border-dashed border-slate-300" />
              <p className="text-slate-600 leading-relaxed">{value.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How are we Unique Section */}
      <section className="relative overflow-hidden bg-white py-20 md:py-28">
        <div aria-hidden="true" className="pointer-events-none absolute -top-32 -left-32 h-96 w-96 rounded-full bg-orange-200/40 blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-6">
          <motion.h2
            {...reveal()}
            className="max-w-4xl text-4xl md:text-5xl font-extrabold tracking-tight leading-[1.1] mb-14"
          >
            How are we <Accent>Unique Special Different Unusual Distinctive</Accent>?
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {uniquePoints.map((point, index) => (
              <motion.p
                key={index}
                {...reveal(index * 0.1)}
                className="rounded-3xl border border-dashed border-slate-300 bg-white/80 p-8 text-lg leading-relaxed text-slate-600"
              >
                {point}
              </motion.p>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="bg-stone-50 py-20 md:py-28 border-t border-slate-200">
        <motion.div {...reveal()} className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-6xl font-extrabold tracking-tight leading-[1.05] mb-8">
            From <Accent>Concept to Final Result</Accent>
          </h2>
          <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
            With in-house department we can take your project from initial concept and proposal right through to final deliverable.
            Our aftercare support ensures your process runs as smoothly and efficiently as possible, safeguarding your investment and giving you peace of mind.
          </p>
        </motion.div>
      </section>

      {/* Contact CTA */}
      <section className="bg-white px-6 py-20 md:py-28">
        <motion.div
          {...reveal()}
          className="relative max-w-6xl mx-auto overflow-hidden rounded-[2rem] bg-slate-900 px-8 py-16 md:py-20 text-center text-white"
        >
          <div aria-hidden="true" className="pointer-events-none absolute -right-10 -top-10 h-56 w-56 rounded-full bg-orange-500/30 blur-3xl" />
          <div aria-hidden="true" className="pointer-events-none absolute inset-3 rounded-[1.5rem] border-2 border-dashed border-orange-400/30" />
          <div className="relative">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-5">
              Ready to Work <span className="text-[#F97316]">Together</span>?
            </h2>
            <p className="text-lg text-slate-300 mb-10 max-w-xl mx-auto">
              Let's discuss your project and bring your vision to life with our expert team.
            </p>
            <a
              href="/contact"
              className="inline-block rounded-xl bg-[#F97316] px-8 py-3.5 text-lg font-semibold text-white transition-colors hover:bg-[#EA580C] focus:outline-none focus-visible:ring-4 focus-visible:ring-orange-400/50"
            >
              Get Started Today
            </a>
          </div>
        </motion.div>
      </section>
    </div>
  )
}

export default CompanyPage